// ─────────────────────────────────────────────────────────────────────────────
// LocationEmailService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for the location emails registry (used by the location-profile
// "Extra Emails" section).
// Endpoints: /v1/location-emails (5 routes — list, getById, create,
// update, remove).
// Source discriminator stays server-side: create/update DTOs intentionally
// do NOT carry `source` — backend stamps 'volta' on create and rejects writes
// on 'erp' rows with HTTP 404 (source-guard).
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { APIResponse } from 'src/app/core/models/api-response';
import { environment } from 'src/environments/environment';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import {
  ICreateLocationEmailPayload,
  ILocationEmail,
  IStructuredApiError,
  IUpdateLocationEmailPayload,
  LocationEmailSource,
  STRUCTURED_ERROR_CODES,
} from '../models/location-email.models';

@Injectable({
  providedIn: 'root',
})
export class LocationEmailService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public list(locationId: number, source?: LocationEmailSource): Observable<APIResponse<ILocationEmail>> {
    let params = new HttpParams().set('location_id', locationId);
    if (source) {
      params = params.set('source', source);
    }
    return this.http.get<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails`, { params });
  }

  // ── List for company ─────────────────────────────────────────────────────────

  /**
   * Fetch every location email under a company in one call (no per-location
   * fan-out). Filters server-side on `customer_id` (location_id.id_company);
   * returns both erp and volta rows across all of the company's locations.
   */
  public listForCompany(customerId: number): Observable<APIResponse<ILocationEmail>> {
    // One-shot company-wide load — request the max page size so a company with
    // many location emails is returned in full rather than the default page of 25.
    const params = new HttpParams().set('customer_id', customerId).set('limit', '1000');
    return this.http.get<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails`, { params });
  }

  // ── Get by id ──────────────────────────────────────────────────────────────

  public getById(id: number): Observable<APIResponse<ILocationEmail>> {
    return this.http.get<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails/${id}`);
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public create(payload: ICreateLocationEmailPayload): Observable<APIResponse<ILocationEmail>> {
    return this.http
      .post<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails`, payload, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public update(id: number, payload: IUpdateLocationEmailPayload): Observable<APIResponse<ILocationEmail>> {
    return this.http
      .post<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails/${id}`, payload, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Remove ─────────────────────────────────────────────────────────────────

  public remove(id: number): Observable<APIResponse<ILocationEmail>> {
    return this.http
      .delete<APIResponse<ILocationEmail>>(`${environment.BASE_URL_API}location-emails/${id}`, { context: skipErrorToast() })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => this.toStructuredError(error))));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Map an HttpErrorResponse to a typed IStructuredApiError so effects can
   * dispatch a typed error action without re-inspecting raw HTTP shape.
   * - 404 → EMAIL_NOT_FOUND sentinel (row no longer exists, refresh UX)
   * - 409 → DUPLICATE (backend returns 409 + scalar message for duplicates)
   * - everything else → generic message fallback
   */
  private toStructuredError(error: HttpErrorResponse): IStructuredApiError {
    if (error.status === 404) {
      return {
        code: STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND,
        message: error?.error?.message || 'Email not found',
      };
    }
    if (error.status === 409) {
      return {
        code: STRUCTURED_ERROR_CODES.DUPLICATE,
        message: error?.error?.message || 'This email already exists',
      };
    }
    return {
      message: error?.error?.message || '',
    };
  }
}
